import { ref } from "vue";

export interface PaginatedType<T> {
    items: T;
    total: number;
}

export const DefaultPaginatedType = Object.freeze<PaginatedType<any>>({
    items: [],
    total: 0,
});

export interface DRFOptionsResponse {
    name: string;
    description: string;
    renders: string[];
    parses: string[];
    actions: {
        [method: string]: DRFOptionsResponseAction;
    };
}

export interface DRFOptionsResponseAction {
    [field: string]: {
        type: string;
        required: boolean;
        read_only: boolean;
        label: string;
        choices?: DRFFieldChoice[];
        child: {
            [field: string]: DRFOptionsResponseAction;
        };
        children: DRFOptionsResponseAction;
    };
}

export interface DRFFieldChoice {
    value: string ;
    display_name: string;
}

export interface VFormFieldChoice<T extends string | number | boolean> {
  title: string;
  subtitle?: string;
  value: T;
  disabled?: boolean;
  meta?: string;
  entity?: any;
}

export interface VFormFieldChoices {
    [field: string]: VFormFieldChoice<string>[];
}

export interface VFormFieldOption {
    name: string;
    label: string;
    disabled: boolean;
    choices: VFormFieldChoice<string | number | boolean>[];
}

export interface VFormOptions {
    [field: string]: VFormFieldOption;
}

export const emptyVFormFieldOption = ref<VFormFieldOption>({
    name: '',
    label: '',
    disabled: false,
    choices: []
});

export function translateFieldChoice(key: string, fieldValue: any, options?: VFormOptions): string {
    if (options && options.hasOwnProperty(key) && options[key].choices.length > 0) {
        const choice = options[key].choices.find(choice => choice.value === fieldValue);
        return choice ? choice.title : fieldValue;
    }

    return fieldValue;
}

export function generateFormOptions(
    formFields: DRFOptionsResponseAction,
    fieldSettings?: { [field: string]: { label: string; disabled: boolean } }
) {
    const formOptions: VFormOptions = {};
    const choices = generateFormChoices(formFields);

    for (const formField in formFields) {
        formOptions[formField] = {
            name: formField,
            label: fieldSettings?.[formField]?.label ?? formFields[formField].label,
            disabled: fieldSettings?.[formField]?.disabled ?? false,
            choices: choices[formField] ?? [],
        };
    }

    return formOptions;
}

export function generateFormChoices(allFormFields: DRFOptionsResponseAction): VFormFieldChoices {
    const formFieldChoices: VFormFieldChoices = {};

    function recursiveGenerateChoices(fields: DRFOptionsResponseAction, parentKey?: string) {
        for (const formField in fields) {
            const currentKey = parentKey ? `${parentKey}.${formField}` : formField;
            const choices = fields[formField]?.choices;
            const childChoices = fields[formField]?.child?.choices;
            const children = fields[formField]?.child?.children;
            const directChildren = fields[formField]?.children;

            if (choices) {
                formFieldChoices[currentKey] = choices.map<VFormFieldChoice<string>>(choice => ({
                    title: choice.display_name,
                    value: choice.value,
                }));
            }

            if (childChoices) {
                // HACK - i really don't know how to do this properly
                formFieldChoices[currentKey] = (childChoices as unknown as DRFFieldChoice[]).map<
                    VFormFieldChoice<string>
                >(choice => ({
                    title: choice.display_name,
                    value: choice.value,
                }));
            }

            if (children) {
                recursiveGenerateChoices(children, currentKey);
            }
            if (directChildren) {
                recursiveGenerateChoices(directChildren, currentKey);
            }
        }
    }
    recursiveGenerateChoices(allFormFields);

    return formFieldChoices;
}
